/**
 * Panneau de suggestions automatiques de conclusions.
 * Apparait au-dessus de la section Conclusions dans rapport-edit.
 * Calcule la distribution des mesures (vert/orange/rouge/rouge sombre)
 * et propose les blocs librairie correspondants.
 */

import { useState } from "react";
import { Sparkles, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb } from "@/core/offline/db";
import { useLibrary, type LibrarySection } from "@/core/hooks/useLibrary";
import {
  computeDistribution,
  computeSuggestions,
  type ConclusionSuggestion,
} from "./conclusion-suggestions";

interface ConclusionSuggestionsProps {
  siteId: string;
  onInsert: (section: LibrarySection) => void;
}

export function ConclusionSuggestions({ siteId, onInsert }: ConclusionSuggestionsProps) {
  const [open, setOpen] = useState(true);
  const { sections: libraryBlocks, loading: libLoading } = useLibrary();

  // Charger les mesures de la campagne active (ou la plus recente)
  const suggestions = useLiveQuery<ConclusionSuggestion[]>(async () => {
    const campagnes = await offlineDb.campagnes
      .where("siteId").equals(siteId).toArray();
    if (campagnes.length === 0) return [];

    const campagne =
      campagnes.find((c) => c.status === "active") ??
      campagnes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

    const mesures = await offlineDb.mesures
      .where("campagneId").equals(campagne.id).toArray();

    const dist = computeDistribution(mesures);
    return computeSuggestions(dist);
  }, [siteId]) ?? [];

  // Pas de mesures : ne rien afficher
  if (!suggestions || suggestions.length === 0) return null;

  // Associer chaque suggestion a son bloc librairie
  const resolvedSuggestions = suggestions
    .map((s) => ({ ...s, block: libraryBlocks.find((b) => b.id === s.blockId) }))
    .filter((s): s is typeof s & { block: LibrarySection } => !!s.block);

  if (libLoading || resolvedSuggestions.length === 0) return null;

  return (
    <div className="mb-4 rounded-xl border border-primary/20 bg-primary/5 overflow-hidden">
      {/* Header cliquable */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-primary/10 transition-colors text-left"
      >
        <Sparkles className="size-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-primary">
            Suggestions automatiques
          </span>
          <span className="ml-2 text-xs text-primary/70">
            {resolvedSuggestions.length} bloc{resolvedSuggestions.length > 1 ? "s" : ""} conseille{resolvedSuggestions.length > 1 ? "s" : ""} d'apres vos mesures
          </span>
        </div>
        {open ? (
          <ChevronUp className="size-4 text-primary/60 shrink-0" />
        ) : (
          <ChevronDown className="size-4 text-primary/60 shrink-0" />
        )}
      </button>

      {/* Liste des suggestions */}
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {resolvedSuggestions.map(({ blockId, reason, block }) => (
            <div
              key={blockId}
              className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{block.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{reason}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onInsert(block)}
                className="shrink-0 gap-1.5 text-xs h-7"
              >
                <Plus className="size-3" />
                Inserer
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
